import { GraphQLClient } from 'graphql-request';
import { AIModel, AIModelProvider } from '../types.ts/ai_prompts';
import {
  ALL_AI_MODELS_QUERY,
  ALL_AI_MODEL_PROVIDERS_QUERY,
  AI_MODEL_BY_ID_QUERY,
} from '@src/gql/ai-prompts-gql';
import { LogManager } from '@src/utility/Logger';

export class AIModelRepository {
  private gqlClient: GraphQLClient;
  private logger = LogManager.getLogger('repository:AIModelRepository');
  private cachedModels: AIModel[] | null = null;

  constructor(gqlClient: GraphQLClient) {
    this.gqlClient = gqlClient;
  }

  async getAllProviders(): Promise<AIModelProvider[]> {
    try {
      const response = (await this.gqlClient.request(
        ALL_AI_MODEL_PROVIDERS_QUERY,
      )) as { allAiModelProviders: AIModelProvider[] };
      return response.allAiModelProviders;
    } catch (err) {
      this.logger.error('Error loading AI model providers', err);
      return [];
    }
  }

  async getAllModels(): Promise<AIModel[]> {
    if (this.cachedModels) return this.cachedModels;
    try {
      const response = (await this.gqlClient.request(
        ALL_AI_MODELS_QUERY,
      )) as { allAiModels: AIModel[] };
      this.cachedModels = response.allAiModels;
      return this.cachedModels;
    } catch (err) {
      this.logger.error('Error loading AI models', err);
      return [];
    }
  }

  async getModelById(id: string): Promise<AIModel | undefined> {
    try {
      const response = (await this.gqlClient.request(AI_MODEL_BY_ID_QUERY, {
        id,
      })) as { aiModel: AIModel };
      return response.aiModel;
    } catch (err) {
      this.logger.error('Error loading AI model by ID', err);
      return undefined;
    }
  }

  async getDefaultModel(): Promise<AIModel | undefined> {
    const models = await this.getAllModels();
    return models.find((m) => m.isDefault);
  }
}
